<template>
  <div
    id="app"
    class="cl-app-surface min-h-screen font-sans text-foreground antialiased"
    :class="{
      'cl-app-with-sidebar': showSidebar,
      'cl-app-cards-nav': showCardsNav,
      'cl-app-tenant-public': isTenantPublicChrome,
    }"
  >
    <Navigation />
    <SidebarNavCards v-if="showCardsNav" />
    <div class="app-content">
      <Sidebar v-if="showSidebar" @sidebar-toggle="handleSidebarToggle" />
      <div class="main-content" :class="{ 'sidebar-open': showSidebar && !sidebarCollapsed, 'sidebar-collapsed': showSidebar && sidebarCollapsed }">
        <router-view v-slot="{ Component, route }">
          <ClAnimatePresence mode="wait">
            <ClMotion
              v-if="Component"
              :key="route.path"
              preset="fade"
              :initial="false"
              class="route-page min-h-full w-full"
            >
              <component :is="Component" />
            </ClMotion>
          </ClAnimatePresence>
        </router-view>
      </div>
    </div>
    <JobProgressCard />
    <WhatsAppSupportWidget />
    <EftProofModal />
  </div>
</template>

<script>
import Navigation from './components/Navigation.vue'
import Sidebar from './components/Sidebar.vue'
import SidebarNavCards from './components/SidebarNavCards.vue'
import JobProgressCard from './components/shared/JobProgressCard.vue'
import WhatsAppSupportWidget from './components/WhatsAppSupportWidget.vue'
import EftProofModal from './components/admin/EftProofModal.vue'
import ClAnimatePresence from './components/ds/ClAnimatePresence.vue'
import ClMotion from './components/ds/ClMotion.vue'
import { mapActions, mapGetters } from 'vuex'
import eventBus from './utils/eventBus'
import { isPublicMarketingRoute } from '@/utils/publicMarketingRoutes'
import { isTenantWorkspaceHost } from '@/utils/saasPlatform'
import {
  DEFAULT_CLINCH_THEME,
  isCardsNavLayout,
  loadBrandThemeFromStorage,
} from '@/theme/brandTheme'

export default {
  name: 'App',
  components: {
    Navigation,
    Sidebar,
    SidebarNavCards,
    JobProgressCard,
    WhatsAppSupportWidget,
    EftProofModal,
    ClAnimatePresence,
    ClMotion
  },
  data() {
    return {
      sidebarCollapsed: false,
      sidebarLayout: DEFAULT_CLINCH_THEME.sidebar.layout,
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated']),
    isTenantPublicChrome() {
      return !this.isAuthenticated && isTenantWorkspaceHost()
    },
    isCardsNav() {
      return isCardsNavLayout(this.sidebarLayout)
    },
    showCardsNav() {
      if (!this.isAuthenticated || !this.isCardsNav) return false
      return !isPublicMarketingRoute(this.$route.path)
    },
    showSidebar() {
      // Show sidebar on authenticated pages, but not on login/register/home
      if (!this.isAuthenticated) {
        return false
      }
      
      const path = this.$route.path
      if (isPublicMarketingRoute(path)) {
        return false
      }
      return true
    }
  },
  methods: {
    ...mapActions(['fetchCurrentUser']),
    handleSidebarToggle(collapsed) {
      this.sidebarCollapsed = collapsed
      // Also emit to event bus for Navigation component
      eventBus.emit('sidebar-state-changed', collapsed)
    },
    syncSidebarLayout(event) {
      const theme = event?.detail?.theme || loadBrandThemeFromStorage()
      this.sidebarLayout =
        theme.sidebar?.layout || DEFAULT_CLINCH_THEME.sidebar.layout
      if (isCardsNavLayout(this.sidebarLayout)) {
        this.sidebarCollapsed = true
        eventBus.emit('sidebar-state-changed', true)
      }
    },
  },
  async mounted() {
    this.syncSidebarLayout()
    if (typeof window !== 'undefined') {
      window.addEventListener('brand-theme-changed', this.syncSidebarLayout)
    }
    // Check if user is authenticated on app load
    try {
      await this.fetchCurrentUser()
    } catch (error) {
      // User not authenticated, which is fine
    }
    
    // Listen for sidebar state changes
    eventBus.on('sidebar-state-changed', (collapsed) => {
      this.sidebarCollapsed = collapsed
    })
    
    // Load sidebar state from localStorage
    const savedState = localStorage.getItem('sidebarCollapsed')
    if (savedState !== null) {
      this.sidebarCollapsed = savedState === 'true'
      eventBus.emit('sidebar-state-changed', this.sidebarCollapsed)
    }
  },
  beforeUnmount() {
    eventBus.off('sidebar-state-changed', this.handleSidebarToggle)
    if (typeof window !== 'undefined') {
      window.removeEventListener('brand-theme-changed', this.syncSidebarLayout)
    }
  },
  watch: {
    $route() {
      // Update sidebar visibility when route changes
      // This ensures sidebar state is maintained across route changes
    },
    isCardsNav(on) {
      if (on) {
        this.sidebarCollapsed = true
        eventBus.emit('sidebar-state-changed', true)
      }
    },
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800;900&family=Inter:wght@400;500;600;700;800&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  overflow-x: clip;
  width: 100%;
}

body {
  margin: 0;
  padding: 0;
  font-family: 'Nunito', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-primary);
  background-color: var(--bg-primary);
  overflow-x: clip;
  width: 100%;
}

#app {
  min-height: 100vh;
  overflow-x: clip;
  width: 100%;
}

/* Compact dashboard chrome (navbar + sidebar header + content offset). */
#app.cl-app-with-sidebar {
  --navbar-height: 64px;
}

#app.cl-app-tenant-public {
  --navbar-height: 50px;
}

.app-content {
  display: flex;
  min-height: calc(100vh - var(--navbar-height, 86px));
  overflow-x: clip;
  width: 100%;
  max-width: 100vw;
}

.app-content.with-sidebar {
  margin-top: var(--navbar-height, 86px); /* Fixed bar; matches public + authenticated nav */
}

.main-content {
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  overflow-x: clip;
  transition: margin-left 0.3s ease;
  min-height: calc(100vh - var(--navbar-height, 86px));
}

.main-content.sidebar-open {
  margin-left: var(--sidebar-width, 260px);
}

.main-content.sidebar-collapsed {
  margin-left: var(--sidebar-collapsed-width, 80px);
}

#app.cl-app-cards-nav .main-content.sidebar-open {
  margin-left: var(--sidebar-collapsed-width, 80px);
}

body.nav-cards-open {
  overflow: hidden;
}

/* When sidebar is not shown, content takes full width */
.main-content:not(.sidebar-open):not(.sidebar-collapsed) {
  margin-left: 0;
  width: 100%;
}

@media (max-width: 768px) {
  .main-content.sidebar-open,
  .main-content.sidebar-collapsed {
    margin-left: 0;
  }
}

/* Button Styles */
.btn-primary {
  background-color: var(--clinch-blue);
  border-color: var(--clinch-blue);
  color: var(--clinch-white);
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background-color: var(--clinch-blue-dark);
  border-color: var(--clinch-blue-dark);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 102, 204, 0.3);
}

.btn-success {
  background-color: var(--whatsapp-green);
  border-color: var(--whatsapp-green);
  color: var(--clinch-white);
  font-weight: 600;
  padding: 0.75rem 2rem;
  border-radius: 5px;
  transition: all 0.3s ease;
}

.btn-success:hover {
  background-color: var(--whatsapp-green-dark);
  border-color: var(--whatsapp-green-dark);
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(37, 211, 102, 0.3);
}

.btn-lg {
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
}

/* Text Colors */
.text-primary {
  color: var(--clinch-blue) !important;
}

.text-success {
  color: var(--whatsapp-green) !important;
}

.text-warning {
  color: var(--clinch-orange) !important;
}

/* Background Colors */
.bg-light {
  background-color: var(--bg-secondary) !important;
}

/* Links */
a {
  color: var(--clinch-blue);
  text-decoration: none;
  transition: color 0.3s ease;
}

a:hover {
  color: var(--clinch-blue-dark);
  text-decoration: underline;
}

/* Smooth Scrolling */
html {
  scroll-behavior: smooth;
}
</style>
